import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useCurrentFiltersStore } from "@/stores/currentFilters";

/* eslint-disable no-unused-vars */
class CurrentMeasurementsService {
  // Zakres cache zdjęć (helpers/vehicleImageCache) — id pomiaru jest unikalne tylko w obrębie bazy bieżącej.
  imageCacheScope = 'current';

  // Flaga dźwięku – true = wyciszone
  silence = false;

  constructor() {
    // Licznik nowych pojazdów (np. do badge na menu)
    this.newCarsCount = 0;
  }

  /**
   * Buduje string zapytania na podstawie filtrów z aktualnego store'a
   * Używane w zapytaniach GET do API
   */
  getQueryString() {
    const filtersStore = useCurrentFiltersStore();
    const filters = filtersStore.filters;
  
    const query = Object.entries(filters)
      .filter(([_, value]) => {
        // Dodaj 0 oraz "0" jako wartości brane pod uwagę
        if (value === 0 || value === "0") return true;
        // Jeżeli tablica, przepuszczaj tylko niepuste
        if (Array.isArray(value)) return value.length > 0;
        // Inne wartości tylko jeśli są truthy
        return Boolean(value);
      })
      .map(([key, value]) => {
        // Tablice serializujemy zawsze jako lista rozdzielona przecinkami (np. locations=1,2,3,
        // laneNumbers=1,2, ctg8p1=2,3,5). Backend (RequestParameter\BaseRequest::strToArrayInt)
        // oczekuje stringa z przecinkami; format key=v1&key=v2 powoduje, że Symfony's parse_str
        // bierze tylko ostatnią wartość i filtry tablicowe zwracają niekompletny wynik.
        if (Array.isArray(value)) {
          return `${key}=${value.join(",")}`;
        }
        // Zwykły przypadek – klucz=wartość
        return `${key}=${value}`;
      })
      .join("&");
  
    return query ? `&${query}` : "";
  }  

  /**
   * Pobierz raport ZIP dla pojedynczego pomiaru
   */
  getVehicleZip(measurementId) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/current-vehicles/${measurementId}/report`
    );
  }

  /**
   * Główna metoda do pobierania pojazdów - najpierw sprawdza czy trzeba odświeżyć
   */
  getAll(lastId) {
    const filter = this.getQueryString();
    if (!filter) {
      return Promise.resolve();
    }

    return fetchWrapper
      .get(`${process.env.VUE_APP_API_URL}/current-vehicles/check?` + filter)
      .then((data) => {
        // Jeżeli refresh=true, pobierz nowe dane
        if (data.refresh === true) {
          fetchWrapper
            .get(
              `${process.env.VUE_APP_API_URL}/current-vehicles/counters?` +
                filter
            )
            .then((data) => {});
          return fetchWrapper.get(
            `${process.env.VUE_APP_API_URL}/current-vehicles?orderDirection=desc&withAxles=true&withImages=true&withShapeSamples=true&withTags=true` +
              filter +
              (lastId ? `&lastId=` + lastId : "")
          );
        }
        // Jeśli nie ma refresh, nie pobieraj ponownie
        return Promise.resolve();
      });
  }

  /**
   * Szybkie sprawdzenie lokalizacji (czy wymaga odświeżenia)
   */
  checkLocations(locationsArray) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/current-vehicles/check?locations=${locationsArray.join(",")}`
    );
  }

  /**
   * Pobierz listę pojazdów z filtrami, paginacją i opcjonalnym lastId (do podczytywania nowych)
   */
  getVehicleList(lastId) {
    const queryString = this.getQueryString().replace(/^&/, '');
    let address = `${process.env.VUE_APP_API_URL}/current-vehicles?${queryString}&withAxles=true&withImages=true&withShapeSamples=true&withTags=true`;

    if (lastId !== null) {
      address += `&lastId=${lastId}`;
    }

    return fetchWrapper.get(address);
  }

  /**
   * Pobierz liczniki (np. czas systemowy, statystyki dla lokalizacji)
   */
  getCounters(locationsArray) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/current-vehicles/counters?locations=${locationsArray.join(",")}`
    );
  }

  /**
   * Szczegóły jednego pojazdu (ze zdjęciami)
   */
  get(id, location) {
    return fetchWrapper.get(
      // Bez `withDataImages` — zdjęcia pobiera osobno komponent VehicleImage, a wrzucanie ich w base64
      // do tej odpowiedzi tylko podwajało transfer i opóźniało wyświetlenie szczegółów.
      `${process.env.VUE_APP_API_URL}/current-vehicles/${id}?locationId=${location}`
    );
  }

  /**
   * Normy EURO pojazdu (dane z zewnętrznej usługi, dociągane osobno po wyświetleniu szczegółów)
   */
  getEuroNorms(id, location) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/current-vehicles/${id}/euro-norms?locationId=${location}`
    );
  }

  /**
   * Pobierz zdjęcie pojazdu
   */
  getImage(id, typeId, location) {
    return fetchWrapper.getBlob(
      `${process.env.VUE_APP_API_URL}/current-vehicles/${id}/images/${typeId}?locationId=${location}`
    );
  }  

  /**
   * Pobierz zdjęcie w wersji ze skalą (np. miniatura)
   */
  getImageWithScale(id, typeId, location) {
    return fetchWrapper.getBlob(
      `${process.env.VUE_APP_API_URL}/current-vehicles/${id}/images/${typeId}?locationId=${location}&scale=0.3`
    );
  }

  /**
   * Włącz/wyłącz dźwięk powiadomienia
   */
  beepToggle(toggle) {
    this.silence = toggle;
  }

  /**
   * Sprawdź czy tablica pojawiła się na VMS (tablica świetlna)
   */
  getPlateVmsStatus(id) {
    return fetchWrapper.get(
      `${process.env.VUE_APP_API_URL}/current-vehicles/${id}/plate-on-vms`
    );
  }

  /**
   * Wyślij tablicę lub wszystkie ciężarówki na VMS
   */
  displayPlateOrAllTrucks(id, allTrucks) {
    return fetchWrapper.post(`${process.env.VUE_APP_API_URL}/current-vehicles/${id}/plate-on-vms?allTrucks=${allTrucks}`);
  }

  /**
   * Wyczyść (usuń) tablicę z VMS
   */
  clearPlateVms(id) {
    return fetchWrapper.delete(`${process.env.VUE_APP_API_URL}/current-vehicles/${id}/plate-on-vms`);
  }
  
  /**
   * Sygnalizacja dźwiękowa pojawienia się nowego pojazdu (Web Audio API)
   */
  beep() {
    if (this.silence) {
      return;
    }
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    gainNode.gain.value = 1;
    oscillator.frequency.value = 1000;
    oscillator.type = "square";

    oscillator.start();

    setTimeout(function () {
      oscillator.stop();
    }, 100);
  }

  /**
   * Getter i setter licznika nowych pojazdów (do badge w menu)
   */
  getNewCarsNumber() {
    return this.newCarsCount;
  }

  setNewCarsNumber(count) {
    this.newCarsCount = count;
  }

  /**
   * Pobierz plik blob ZIP (raport z API)
   */
  fetch(zip) {
    let zipNoStart = zip.includes("api/v1") ? zip.substr(7) : zip;
    return fetchWrapper.getBlob(process.env.VUE_APP_API_URL + zipNoStart);
  }
}

// Eksport singletona serwisu – korzysta z niego cały frontend
export default new CurrentMeasurementsService();
